import { get } from '@vercel/edge-config';

import { SOCIAL_SPINE_FLAG_DEFAULTS } from '@/lib/social-publishing/defaults';

export type ExpansionFlag =
  | 'expansion_1_generate_all_plan'
  | 'expansion_2_daily_briefing'
  | 'expansion_3_voice_ingestion'
  | 'expansion_4_url_to_pieces'
  | 'expansion_5_photo_clustering';

// Pre-launch posture: ship-it-all-now. Expansion features default ON
// everywhere. Edge Config is consulted as a prod kill-switch — if a flag
// is explicitly set to `false` there, we honor it. Anything else (unset,
// no Edge Config wired, network error) → ON.
const DEFAULT_FLAGS: Record<ExpansionFlag, boolean> = {
  expansion_1_generate_all_plan: true,
  expansion_2_daily_briefing: true,
  expansion_3_voice_ingestion: true,
  expansion_4_url_to_pieces: true,
  expansion_5_photo_clustering: true,
};

function isEdgeConfigConfigured(): boolean {
  return Boolean(process.env.EDGE_CONFIG);
}

/**
 * Build-time snapshot of every client-visible flag.
 *
 * Each entry MUST stay a literal `import.meta.env.VITE_*` member expression —
 * that is the only shape Vite's define plugin rewrites into the browser
 * bundle. An optional-chained or computed read (`import.meta?.env?.[key]`) is
 * never inlined, which is how a flag can silently evaluate to its default in
 * production while looking correct in dev.
 *
 * The whole read is wrapped because this module is also loaded on the server,
 * where `import.meta.env` is not guaranteed to be a plain object. Vite's module
 * runner installs a Proxy whose `get` trap unconditionally throws
 * `Dynamic access of "import.meta.env" is not supported`
 * (`vite/dist/node/module-runner.js` → `createDefaultImportMeta`), and the
 * Workflow DevKit step runtime has crashed on browser-only `import.meta.env`
 * reads before (see the comment in `src/lib/workflows/steps/save-result.ts`).
 * Under Nitro's dev worker today it *is* a populated plain object — verified by
 * runtime probe on 2026-07-31 — but nothing in the toolchain guarantees that,
 * and a throw here 500s `POST /api/generate-image`, which reads as an image
 * quota failure rather than an env bug.
 */
const INLINED_CLIENT_FLAGS: Record<string, unknown> = (() => {
  try {
    return {
      VITE_FEATURE_PLAN_RESUME: import.meta.env.VITE_FEATURE_PLAN_RESUME,
      VITE_FEATURE_VIDEO_RESUME: import.meta.env.VITE_FEATURE_VIDEO_RESUME,
      VITE_FEATURE_TEAM_INVITES: import.meta.env.VITE_FEATURE_TEAM_INVITES,
      VITE_FEATURE_CUSTOM_WORKFLOWS: import.meta.env.VITE_FEATURE_CUSTOM_WORKFLOWS,
      VITE_FEATURE_SOCIAL_SPINE_FEED: import.meta.env.VITE_FEATURE_SOCIAL_SPINE_FEED,
      VITE_FEATURE_TARGET_NATIVE_COVER_EDITOR: import.meta.env
        .VITE_FEATURE_TARGET_NATIVE_COVER_EDITOR,
      VITE_FEATURE_IMAGE_COST_SAVING: import.meta.env.VITE_FEATURE_IMAGE_COST_SAVING,
      VITE_FEATURE_MEDIA_JOB_DIALOG: import.meta.env.VITE_FEATURE_MEDIA_JOB_DIALOG,
    };
  } catch {
    return {};
  }
})();

/**
 * Resolution order, so one flag definition works in every runtime:
 *  1. `process.env[key]` — the live value on Nitro dev and on Vercel (both
 *     expose the `VITE_*` vars there); Vite defines `process.env` to `{}` in
 *     the browser bundle, so this step is inert on the client.
 *  2. a guarded live `import.meta.env[key]` — picks up `vi.stubEnv` in tests
 *     and the real object in the browser.
 *  3. the build-time snapshot above — the guaranteed-inlined browser value.
 */
function readEnvFlag(key: string): string | undefined {
  const fromProcess = typeof process !== 'undefined' && process.env ? process.env[key] : undefined;
  if (fromProcess !== undefined) return fromProcess;
  let live: unknown;
  try {
    live = (import.meta as unknown as { env?: Record<string, unknown> }).env?.[key];
  } catch {
    live = undefined;
  }
  const value = live ?? INLINED_CLIENT_FLAGS[key];
  return value === undefined || value === null ? undefined : String(value);
}

/** Default-ON flag: only an explicit `false`/`0` turns it off. */
function isEnabledUnlessDisabled(key: string): boolean {
  const raw = readEnvFlag(key);
  if (raw === undefined || raw === '') return true;
  const normalized = raw.toLowerCase();
  return normalized !== 'false' && normalized !== '0';
}

export async function isExpansionEnabled(flag: ExpansionFlag): Promise<boolean> {
  if (!isEdgeConfigConfigured()) return DEFAULT_FLAGS[flag];
  try {
    const value = await get<boolean>(flag);
    // Explicit false in Edge Config = kill-switch. Everything else = ON.
    return value !== false;
  } catch {
    return DEFAULT_FLAGS[flag];
  }
}

export function isPlanResumeEnabled(): boolean {
  return isEnabledUnlessDisabled('VITE_FEATURE_PLAN_RESUME');
}

export function isVideoResumeEnabled(): boolean {
  return isEnabledUnlessDisabled('VITE_FEATURE_VIDEO_RESUME');
}

/**
 * Routes image/video generation to `JobDialog` — its own surface — instead
 * of the chat composer. Defaults OFF: the composer path
 * (`CreativeChat.handleSend` → agent turn → `generate_image` / `generate_video`)
 * is the shipping path and stays fully working until this one is verified on a
 * live run. Set `VITE_FEATURE_MEDIA_JOB_DIALOG=true` in `.env.local` to switch.
 */
export function isMediaJobDialogEnabled(): boolean {
  const raw = readEnvFlag('VITE_FEATURE_MEDIA_JOB_DIALOG');
  if (raw === undefined || raw === '') return false;
  const normalized = raw.toLowerCase();
  return normalized === 'true' || normalized === '1';
}

export function isTeamInvitesEnabled(): boolean {
  return isEnabledUnlessDisabled('VITE_FEATURE_TEAM_INVITES');
}

// Hidden until custom-workflow authoring milestone — see usability checkpoint 2026-05-06.
// Defaults OFF (inverse of the other client flags). Set VITE_FEATURE_CUSTOM_WORKFLOWS=true
// in .env.local to surface the "+ New workflow" CTAs again.
export function isCustomWorkflowsEnabled(): boolean {
  const raw = readEnvFlag('VITE_FEATURE_CUSTOM_WORKFLOWS');
  if (raw === undefined || raw === '') return false;
  const normalized = raw.toLowerCase();
  return normalized === 'true' || normalized === '1';
}

/**
 * Server-only release gate for the Brief copy revision protocol.
 *
 * This defaults closed: an unset, malformed, browser-provided, or unavailable
 * value never authorizes Concept-bound copy generation. Task 8A keeps it off
 * through database activation and the old-instance drain.
 */
export function isBriefCopyRevisionsEnabled(): boolean {
  if (typeof process === 'undefined' || !process.env) return false;
  const raw = process.env.FEATURE_BRIEF_COPY_REVISIONS;
  if (raw === undefined || raw === '') return false;
  const normalized = raw.trim().toLowerCase();
  return normalized === 'true' || normalized === '1';
}

/**
 * The Instagram planner is enabled after exact-account target identity,
 * account-scoped native history, and rank-only persistence shipped. The env
 * override remains the emergency rollback switch.
 */
export function isSocialSpineFeedEnabled(): boolean {
  const raw = readEnvFlag('VITE_FEATURE_SOCIAL_SPINE_FEED');
  if (raw === undefined || raw === '') {
    return SOCIAL_SPINE_FLAG_DEFAULTS.SOCIAL_SPINE_FEED;
  }
  const normalized = raw.toLowerCase();
  return normalized === 'true' || normalized === '1';
}

/**
 * Target-native cover editing defaults on after the target-authority cutover.
 * The environment value remains an emergency UI kill-switch; automatic cover
 * materialization and exact-target dispatch do not depend on this flag.
 */
export function isTargetNativeCoverEditorEnabled(): boolean {
  return isEnabledUnlessDisabled('VITE_FEATURE_TARGET_NATIVE_COVER_EDITOR');
}

/**
 * Cost-saving posture for `selectRealismModel`'s auto image-model routing.
 * Google Cloud credits haven't landed yet (2026-07-28) and Nano Banana Pro is
 * materially more expensive than Flash, so this defaults ON: the logo-only
 * and "2+ generic references" auto-routing cases fall through to Flash
 * instead of Pro. Identity (person) and product-fidelity guardrails, explicit
 * `premium`, and `realismTemplateId` routing are untouched regardless of this
 * flag — only the two lower-stakes catch-alls are affected. Set
 * VITE_FEATURE_IMAGE_COST_SAVING=false once GCP credits are confirmed to
 * restore full Pro-first routing.
 */
export function isImageCostSavingModeEnabled(): boolean {
  return isEnabledUnlessDisabled('VITE_FEATURE_IMAGE_COST_SAVING');
}

export async function getAllExpansionFlags(): Promise<Record<ExpansionFlag, boolean>> {
  if (!isEdgeConfigConfigured()) return { ...DEFAULT_FLAGS };
  try {
    const { getAll } = await import('@vercel/edge-config');
    const all = await getAll();
    // For each flag: explicit `false` kills it, anything else (unset / true / non-bool) is ON.
    return {
      expansion_1_generate_all_plan: all?.expansion_1_generate_all_plan !== false,
      expansion_2_daily_briefing: all?.expansion_2_daily_briefing !== false,
      expansion_3_voice_ingestion: all?.expansion_3_voice_ingestion !== false,
      expansion_4_url_to_pieces: all?.expansion_4_url_to_pieces !== false,
      expansion_5_photo_clustering: all?.expansion_5_photo_clustering !== false,
    };
  } catch {
    return { ...DEFAULT_FLAGS };
  }
}

// ---------------------------------------------------------------------------
// Temporary surface suppression (founder, 2026-08-23)
// ---------------------------------------------------------------------------
//
// 🔴 THE CODE BEHIND THESE TWO SURFACES IS NOT DEAD AND MUST NOT BE DELETED.
//
// Founder instruction, verbatim: _"I want you to temporarily hide the comments
// because it's just way too messy on the app right now. The feed page needs to
// be hidden... We'll bring that back if customers ask for it. Definitely don't
// delete it because we spent a lot of time on it... Make sure it doesn't get
// deleted with any kind of code cleanups or anything."_
//
// Both surfaces stay fully wired — routes, components, hooks, tests all remain
// — they are simply not offered in the navigation. Re-enabling either is a
// one-line change to the constant below, NOT a rebuild.
//
// This is deliberately a plain constant rather than an env flag: an env flag
// reads as an operational toggle someone might flip by accident, and the
// dead-code sweepers (`dead-code-finder-weekly`) follow references — a constant
// referenced from the router and the tab strip keeps every downstream module
// reachable in the import graph, which an env-gated early return would not
// guarantee.
//
// ⚠️ To any automated cleanup pass reading this: `FeedPage`, the
// `src/components/publishing/feed-view/` lane, and the Engagement dock are
// SUPPRESSED, not abandoned. Do not remove them.

/** The Instagram-grid Feed planner tab inside Publishing. Suppressed 2026-08-23. */
export const PUBLISHING_FEED_TAB_VISIBLE = false;

/** The Engagement (comments/inbox) destination in the sidebar. Suppressed 2026-08-23. */
export const ENGAGEMENT_NAV_VISIBLE = false;
